import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { parseMoneyToCents } from "../../../../../utils/money";
import { AmountFigure, AmountFigureDivider, type AmountFigureSize } from "./AmountFigure";
import { CashTipPanel } from "./CashTipPanel";
import { MixedTipPanel } from "./MixedTipPanel";
import { TipCard } from "./TipCard";
import { useTipDueSummary } from "./tipDue";
import type { BillCloseTip } from "./useBillCloseTip";

interface CloseTipColumnProps {
  closeTip: BillCloseTip;
  billMethodId: number | null;
  totalAmount: string;
  methodNameOf: (methodId: number) => string;
  /** „Ресто при плащане в брой" (Конфигурация → Маркиране). */
  cashChangeEnabled: boolean;
  cashReceived: string;
  onCashReceivedChange: (value: string) => void;
  mixedCashAmount: number;
  mixedCardAmount: number;
  onMixedCashChange: (cash: number) => void;
  onMixedCardChange: (card: number) => void;
  /** Телефон — сумата и картата една под друга, без помощника за ресто. */
  isNarrow: boolean;
  disabled: boolean;
}

/**
 * Лявата колона на „Закрий" с бакшиш — дизайнът „Закрий" (claude.ai/design):
 * „В брой" с ресто → `CashTipPanel`; „Смесено" → `MixedTipPanel`; всичко
 * друго (карта, банка, „В брой" без ресто) → „Общо за плащане", „+ Добави
 * бакшиш", подсказката за терминала под него; отворен бакшиш — „Общо за
 * плащане" (поръчаното) и „Бакшиш" една до друга, под тях полето и
 * клавиатурата (`TipEntryCard`), а дължимото с бакшиша е в бутона
 * „Закриване · X EUR". Бакшиш има само при метод, избран в настройките — в
 * брой и при „Смесено" няма (там картата е само за записания при
 * закриването бакшиш на повторния опит).
 *
 * „Общо"/„Общо за плащане" е дължимото С МЕТОДА НА СМЕТКАТА: бакшишът (с
 * метода на сметката) влиза в него; записан с друг метод — пише се отделно.
 */
export function CloseTipColumn({
  closeTip,
  billMethodId,
  totalAmount,
  methodNameOf,
  cashChangeEnabled,
  cashReceived,
  onCashReceivedChange,
  mixedCashAmount,
  mixedCardAmount,
  onMixedCashChange,
  onMixedCardChange,
  isNarrow,
  disabled,
}: CloseTipColumnProps) {
  const { t } = useTranslation();
  const { isTablet } = useViewport();
  // Таблет с двете колони (модал 560) — сумите са по-дребни, за да се
  // съберат една до друга в лявата колона; телефонът е на цяла ширина.
  const figureSize: AmountFigureSize = isNarrow ? "phone" : isTablet ? "compact" : "large";
  const orderedCents = parseMoneyToCents(totalAmount) ?? 0;
  // Повторен опит: записаният бакшиш със същия метод също влиза в „Общо".
  const { dueCents, tipCents, note: dueNote } = useTipDueSummary({
    closeTip,
    orderedCents,
    billMethodId,
    methodNameOf,
  });

  const tipCard = (
    <TipCard
      closeTip={closeTip}
      disabled={disabled}
    />
  );
  const isTipOpened = closeTip.cardMode === "entry" && closeTip.tipEntry.isOpened;
  // Отворен бакшиш: поръчаното и бакшишът една до друга (дизайнът „Закрий");
  // иначе — дължимото и редът под него (записан бакшиш при повторен опит).
  const totals = isTipOpened ? (
    <div className="flex items-stretch">
      <AmountFigure
        label={t("closeTable.totalToPay")}
        cents={orderedCents}
        size={figureSize}
        className="text-pos-accent"
      />
      <AmountFigureDivider size={figureSize} />
      <AmountFigure
        label={`${t("closeTable.tip.title")}:`}
        cents={Math.max(0, tipCents)}
        size={figureSize}
        // Като рестото: без бакшиш — бледо 0.00, с бакшиш — зелено.
        className={tipCents > 0 ? "text-pos-served" : "text-pos-faint"}
      />
    </div>
  ) : (
    <div className="flex flex-col">
      <AmountFigure
        label={t("closeTable.totalToPay")}
        cents={dueCents}
        size={figureSize}
        className="text-pos-accent"
      />
      {dueNote && (
        <div className="mt-1.5 text-[13px] font-medium leading-5 text-pos-soft2">{dueNote}</div>
      )}
    </div>
  );

  // „Смесено" — новият панел и на телефона (полетата над методите, като бакшиша).
  if (billMethodId === 3) {
    return (
      <MixedTipPanel
        dueCents={dueCents}
        totalAmount={totalAmount}
        cashAmount={mixedCashAmount}
        cardAmount={mixedCardAmount}
        onCashChange={onMixedCashChange}
        onCardChange={onMixedCardChange}
        tipSlot={tipCard}
        totalNote={dueNote}
        figureSize={figureSize}
        disabled={disabled}
      />
    );
  }

  // Телефон: дължимото + картата; методите са под колоната.
  if (isNarrow) {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {totals}
        {tipCard}
      </div>
    );
  }

  if (billMethodId === 1 && cashChangeEnabled) {
    return (
      <CashTipPanel
        dueCents={dueCents}
        received={cashReceived}
        onReceivedChange={onCashReceivedChange}
        tipCard={tipCard}
        totalNote={dueNote}
        figureSize={figureSize}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {totals}
      {/* empty:hidden — без бутон за бакшиш (методът не го носи) няма празнина */}
      <div className="mt-5 empty:hidden">{tipCard}</div>
      {/* Подсказката за терминала — само при „С карта", под „+ Добави
          бакшиш"; при отворен бакшиш я няма (дизайнът) */}
      {billMethodId === 2 && !isTipOpened && (
        <div className="mt-4 text-[13.5px] font-medium text-pos-soft">
          {t("closeTable.cardTerminalHint")}
        </div>
      )}
    </div>
  );
}
